/**
 * Product Vue Component
 * ------------------------------------------------------------------------------
 * Main template in sections/product.liquid
 * Can be reused for other instances of a product
 */

import axios from 'axios';
import Slick from 'vue-slick';
import { getUrlWithVariant } from '@shopify/theme-product-form';

import ImageZoom from './image-zoom.vue';
import ReadMore from './read-more.vue';
import Accordion from './accordion.vue';
import Collapsible from './collapsible.vue';
import ButtonSpinner from './button-spinner.vue';

import { app } from '../app.js';
import { storefrontRequestConfig } from "../utils/api";
import { removeParameterFromUrl, waitForElement } from '../utils/helpers.js';



export default {
    components: {
        Slick,
        ImageZoom,
        ReadMore,
        Accordion,
        Collapsible,
        ButtonSpinner
    },

    props: {
        productHandle: {
            type: String,
            required: true
        },
        isProductPage: {
            type: Boolean,
            default: false
        },
        isReserveProduct: {
            type: Boolean,
            default: false
        },
        selectedOrFirstAvailableVariant: {
            type: Object
        }

    },

    data() {
        return {
            isLoading: false,
            isSubmitting: false,
            product: null,
            options: [],
            currentVariant: null,
            quantity: 1,
            colorSwatches: [],
            sizeSwatches: [],
            isProductReserved: false,
            currentSlide: 0,
            mainSlickOptions: {
                slidesToShow: 1,
                focusOnSelect: true,
                arrows: false,
                asNavFor: '.gallery__nav'
            },
            navSlickOptions: {
                slidesToShow: 5,
                variableWidth: true,
                focusOnSelect: true,
                asNavFor: '.gallery__main'
            }
        }
    },

    computed: {
        cart () {
            return app.cart
          },
        isSale() {
            if (this.currentVariant) {
                return this.currentVariant?.compare_at_price > this.currentVariant?.price
            } else {
                return this.product?.compare_at_price > this.product?.price
            }
        }
    },

    created() {
        this.isLoading = true;

        axios.get(`/products/${this.productHandle}.js`).then((response) => {
            // console.log('product', response.data);
            this.product = response.data;
            // if the URL has ?variant=[variant.id], select that variant
            let queryStringValues = new URLSearchParams(window.location.search);
            if (queryStringValues.has('variant')) {
                let variantFromQueryString = this.product.variants.find((variant) => {
                    return variant.id == queryStringValues.get('variant');
                });
                if (variantFromQueryString) this.currentVariant = variantFromQueryString;

            } else if (this.product.variants.length == 1) {
                this.currentVariant = this.product.variants[0];
            } else {
                this.currentVariant = this.selectedOrFirstAvailableVariant;
            }


            if (this.currentVariant) {
                for (let i = 1; i <= this.product.options.length; i++) {
                    this.options.push(this.currentVariant['option' + i]);
                }

                this.updateVariant();

            } else {
                for (let i = 1; i <= this.product.options.length; i++) {
                    this.options.push('');
                }
            }
            // Product option size
            let sizeOption = this.product.options.find(option => (option.name).toLowerCase() == 'size');
                if(sizeOption != null) {
                    // Build swatch array based on variant sizedsadadada
                    sizeOption.values.forEach( size => {
                        let variantSize = this.product.variants.find( variant => variant.options.includes(size));
                        // Set featured images against Variants
                        if(variantSize.available) {
                            this.sizeSwatches.push(variantSize);
                        }
                })
            }  


          
        }).catch((error) => {
            console.log(error);

        }).then( async () => {
            this.isLoading = false;
          
            const metarequestSiblingIdsBody =
            `{
                products(first: 1, query:"id:${this.product.id}") {
                  edges {
                    node {
                      id
                      title
                      handle
                      availableForSale
                      images(first: 10) {
                          edges {
                              node {
                                  url
                              }
                          }
                      }
                      siblingIds: metafield(namespace: "product.info", key: "sibling_ids") {
                         value
                      }
                      colourHex: metafield(namespace: "product.info", key: "colour_hex") {
                        value
                      }
                    }
                  }
                }
              }`

          
            const productInfo =  await this._getProductInfo([], metarequestSiblingIdsBody);

            const self = productInfo  && {
                id: productInfo.id,
                title: productInfo.title,
                handle: productInfo.handle,
                available: productInfo.availableForSale,
                colourHex: productInfo.color
            };
              
            this.colorSwatches.push(self);

            const allSiblingGraphqlIds = productInfo.siblings?.map((productId) => '"' + btoa("gid://shopify/Product/" + productId) + '"');
            const allColourHexSiblingsIdsBody  = `{
                nodes(ids: [${allSiblingGraphqlIds}]) {
                  ... on Product {
                    id
                    title
                    handle
                    availableForSale
                    images(first: 10) {
                        edges {
                            node {
                                url
                            }
                        }
                    }
                    colourHex: metafield(namespace: "product.info", key: "colour_hex") {
                      value
                    }
                  }
                }
              }`;

            const allColourHexSiblings =  await this._getColourHexSiblings([], allColourHexSiblingsIdsBody);

            for (const node of allColourHexSiblings) {
                if (node) {
                    const sibling = {
                        id: parseInt(node.id.replace("gid://shopify/Product/", "")),
                        title: node.title,
                        handle: node.handle,
                        available: node.availableForSale,
                        images: node.images?.edges.map((edge) => edge.node.url),
                        colourHex: node.colourHex?.value,
                    };
                    
                    this.colorSwatches.push(sibling);
                }
            }
  
        });
    },

    async mounted() {
        if (this.$refs.main) {
            this.$refs.main.$on('afterChange', this.handleAfterChange);
        }

        const modalProductInfo = await waitForElement('#size-guide-modal_product-info')
        const sizeGuideModalInner = await waitForElement('.ks-chart-individual')

        if (sizeGuideModalInner) {
            sizeGuideModalInner.insertBefore(modalProductInfo, sizeGuideModalInner.firstChild)
            modalProductInfo.classList.add('d-flex')
        }


        // Vertical gallery nav -
        // Fix for Slick nav slider disappearing if change happens before everything is loaded and parsed
        // window.addEventListener("load", () => {
        //     const element = document.querySelector('.gallery__nav .slick-track');
        //     if (element) element.style.transform = "translate3d(0px, 0px, 0px)";
        // });
    },

    methods: {

        async _getProductMetafields(productMetafields, metarequestBody) {
            return axios
              .post(`https://${Shopify.shop}/api/graphql`, metarequestBody, storefrontRequestConfig)
              .then(async ({ data, config }) => {
                if (data.errors) {
                  console.log(data.errors);
                  return [];
                }
    
                productMetafields = [...productMetafields, ...data.data.nodes];
      
              
                return productMetafields;
                
              })
              .catch((error) => {
                console.log(error);
                return [];
              });
        },

        async _getProductInfo (productMetafields, metarequestBody) {
            return axios
              .post(`https://${Shopify.shop}/api/graphql`, metarequestBody, storefrontRequestConfig)
              .then(async ({ data, config }) => {
                if (data.errors) {
                  console.log(data.errors);
                  return [];
                }
     
                const siblingObject = {
                   color: data && data.data.products.edges[0].node.colourHex?.value,
                   siblings: data && data.data.products.edges[0].node.siblingIds?.value.split('|').slice(1),
                   title: data && data.data.products.edges[0].node.title,
                   handle: data && data.data.products.edges[0].node.handle,
                   available:  data && data.data.products.edges[0].node.availableForSale,
                   images:  data && data.data.products.edges[0].node.images?.edges.map((edge) => edge.node.url),
                   id: data && data.data.products.edges[0].node.id,
               
                } 
                return siblingObject;
                
              })
              .catch((error) => {
                console.log(error);
                return [];
              });
        },

        async  _getColourHexSiblings (productMetafields, metarequestBody) {
            return axios
              .post(`https://${Shopify.shop}/api/graphql`, metarequestBody, storefrontRequestConfig)
              .then(async ({ data, config }) => {
                if (data.errors) {
                  console.log(data.errors);
                  return [];
                }
         
                return data.data.nodes;
                
              })
              .catch((error) => {
                console.log(error);
                return [];
              });
        },

       
      
        handleModalClose() {
            this.isProductReserved = false
        },
        reserveProduct() {
            this.isProductReserved = !this.isProductReserved;
        },
        handleAfterChange(event, slick, currentSlide) {
            this.currentSlide = currentSlide;
        },

        updateVariant(v = null) {
            let variant = null;

            if (v) {
                variant = v;
            }else{
                variant = this._findVariant();
            }

            this._setVariant(variant);
        },

        async handleAddToCart() {
            let lineItemProperties;
            if (this.currentVariant) {
                this.isSubmitting = true;

                if(this.isReserveProduct){
                    lineItemProperties = {
                        _reserved_store: 'true'
                    }
                    
                } else {
                    lineItemProperties = {
                        _reserved_store: ''
                    }
                }
               

                await app.addToCart(this.currentVariant.id, this.quantity, lineItemProperties).then((response) => {

                }).catch((error) => {
                    console.log('error', error);
                    alert('Cannot reach the server at this time, please try again later.');
                });

                this.isSubmitting = false;
                this.isProductReserved = false;
            }
        },

        // When product has multiple options, we need to find the associated variant based on those options
        _findVariant() {
            for (let i = 0; i < this.product.variants.length; i++) {
                if (this.product.variants[i].option1 && this.product.variants[i].option1 != this.options[0]) continue;
                if (this.product.variants[i].option2 && this.product.variants[i].option2 != this.options[1]) continue;
                if (this.product.variants[i].option3 && this.product.variants[i].option3 != this.options[2]) continue;

                return this.product.variants[i];
            }

            return null;
        },

        _setVariant(variant) {
            if (!variant) {
                this.currentVariant = null;
                removeParameterFromUrl('variant');
                return;
            }

            this.currentVariant = variant;

            if (this.isProductPage) {
                if (variant.featured_image && this.product.images.length > 1)
                    this.$refs.main.goTo(variant.featured_image.position - 1);

                let url = getUrlWithVariant(window.location.href, variant.id);
                window.history.replaceState({path: url}, '', url);
            }
        }
    }
};